import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { IWorkflow, IWorkflowActivity, IWorkflowActivityMetaData } from './models/workflow.model';
import { IMAGE_QUALITY } from './capture-strings';
import { SanitizationService } from '../sanitization/sanitization.service';

@Injectable({
  providedIn: 'root'
})
export class CaptureWorkflowService {

  private logger: Logger;
  public workflow: IWorkflow;
  public workflowList: Array<IWorkflow>;
  public imagesArray: Array<any>;


  constructor(
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private sanitizationService: SanitizationService
  ) {
    this.logger = this.loggingService.getLogger("[CaptureWorkflowService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
   }

// ############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		GET_WORKFLOWS            #########################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

  public getWorkflows(workflowsUrl): Observable<any> {
    const urlForMobileWorkflows = workflowsUrl + '?page=0&pageSize=20&direction=ASC&sort=NAME&sources=PHONE'
    return this.httpClient.get<any>(urlForMobileWorkflows).pipe(
      retry(1),
      map((response: any) => this.createWorkflowList(response)),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('getWorkflows() error');
        return throwError(() => error);
      })
    );
  }

  private createWorkflowList(response) {
    let workflowList:Array<IWorkflow> = [];
    if (response._embedded && response._embedded['px:workflowResources']) {
      for (let workflowResource of response._embedded['px:workflowResources']) {
        workflowList.push(this.setWorkflowData(workflowResource));
      }
    }
    return workflowList;
  }

  private setWorkflowData(workflowResource) {
    const refactoredWorkflow: IWorkflow = {
      links: {
        groups: null,
        scans: null,
        test: null,
        self: null
      },
      active: workflowResource['active'],
      created: workflowResource['created'] ? workflowResource['created'] : null,
      destinations: workflowResource['destinations'] ? workflowResource['destinations'] : [],
      fileType: this.setFileType(workflowResource),
      imageQuality: this.setImageQuality(workflowResource),
      name: this.sanitizeDisplayField(workflowResource['name']),
      ocrConfiguration: this.setOcrConfiguration(workflowResource),
      workflowActivities: this.setWorkflowActivityList(workflowResource)
    }
    if (workflowResource._links) {this.setWorkflowLinks(refactoredWorkflow, workflowResource._links)};
    return refactoredWorkflow;
  }

  private setFileType(workflowResource): string {
    let fileType = null;
    const workflowActivities : any[] = workflowResource['_embedded']['workflowActivities'];
    if (workflowActivities.some(a => a.type === "GENERATE_SEARCHABLE_PDF")) {
      fileType = 'PDF'
    } else if (workflowActivities.some(a => a.type === "GENERATE_DOCX")) {
      fileType = 'MicrosoftWord'
    } else {
      fileType = 'JPG'
    }
    return fileType;
  }

  private setImageQuality(workflowResource): string {
    let imageQuality: string = null;
    switch (workflowResource['outputImageQuality']) {
      case 'MIN':
        imageQuality = IMAGE_QUALITY.MIN;
        break;
      case 'MEDIUM':
        imageQuality = IMAGE_QUALITY.MEDIUM;
        break;
      case 'MAX':
        imageQuality = IMAGE_QUALITY.MAX;
        break;
    }
    return imageQuality;
  }

  private setOcrConfiguration(workflowResource): string {
    return workflowResource['_embedded']['workflowActivities'][1]['ocrConfiguration'];
  }

  private setWorkflowLinks(workflow, links) {
    if (!links) {
      return;
    }

    const linkMappings: Array<{ key: string; target: (url: string | null) => void }> = [
      { key: 'px:groups', target: (url) => workflow.links.groups = url },
      { key: 'px:scans', target: (url) => workflow.links.scans = url },
      { key: 'px:test', target: (url) => workflow.links.test = url },
      { key: 'self', target: (url) => workflow.links.self = url }
    ];

    linkMappings.forEach(({ key, target }) => {
      const href = links[key]?.href;
      if (href) {
        target(this.sanitizationService.validateApiUrl(href));
      }
    });
  }

  /**
   * Safely sanitizes non-display text fields from API responses to prevent XSS.
   * Strips HTML tags and dangerous characters. Use for fields NOT shown in templates.
   */
  private sanitizeTextField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeTextInput(value);
    return sanitized || null;
  }

  /**
   * Sanitizes user-provided text fields that will be displayed in templates.
   * Preserves all characters so they render as visible plain text.
   */
  private sanitizeDisplayField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeDisplayText(value);
    return sanitized || null;
  }

  private setWorkflowActivityList(workflowResource) {
    let workflowActivityList: Array<IWorkflowActivity> = [];
    if (workflowResource._embedded['workflowActivities']) {
      for (let workflowActivity of workflowResource._embedded['workflowActivities']) {
        workflowActivityList.push(this.setWorkflowActivity(workflowActivity))
      }
    }
    return workflowActivityList;
  }

  private setWorkflowActivity(workflowActivity) {
    const activity: IWorkflowActivity = {
      links: {
        self: this.sanitizationService.validateApiUrl(workflowActivity._links?.['self']?.href),
      },
      metadata: this.setMetaDataList(workflowActivity),
      stage: this.sanitizeTextField(workflowActivity['stage']),
      type: this.sanitizeTextField(workflowActivity['type'])
    }
    return activity;
  }

  private setMetaDataList(workflowActivity) {
    const metaDataCustomFields = workflowActivity['_embedded']
                               && workflowActivity['_embedded']['metadataProfile']
                               && workflowActivity['_embedded']['metadataProfile']['_embedded']
                               && workflowActivity['_embedded']['metadataProfile']['_embedded']['customFields']
                               ? workflowActivity['_embedded']['metadataProfile']['_embedded']['customFields'] : null;

    let metaDataList = [];
    if (metaDataCustomFields && metaDataCustomFields.length > 0) {
      for (let customField of metaDataCustomFields) {
        metaDataList.push(this.setCustomMetaData(customField))
      }
    }
    return metaDataList;
  }

  private setCustomMetaData(customField) {
    const metaDataField: IWorkflowActivityMetaData = {
      defaultValue: this.sanitizeDisplayField(customField['defaultValue']),
      displayName: this.sanitizeDisplayField(customField['displayName']),
      id: this.sanitizeTextField(customField['id']),
      name: this.sanitizeDisplayField(customField['name']),
      required: customField['required'] ? customField['required'] : false,
      type: this.sanitizeTextField(customField['type']),
      validation: this.sanitizeTextField(customField['validation'])
    }
    return metaDataField;
  }
}
